import { useState } from 'react';

export interface DataFetcherState<T> {
  isFetching: boolean;
  isFetched: boolean;
  isErrored?: boolean;
  data?: T;
}

export interface Options {
  append?: boolean;
}

export type DataFetcherReturn<T> = [
  DataFetcherState<T>,
  (...args: any) => Promise<void>,
  () => void,
];

export default function useDataFetcher<T>(
  fetch: (...args: any) => Promise<T>,
  options?: Options,
): DataFetcherReturn<T> {
  const [state, setState] = useState<DataFetcherState<T>>({
    isFetching: false,
    isErrored: false,
    isFetched: false,
  });

  const append = options ? options.append : false;

  async function asyncFetch(...args: any): Promise<void> {
    setState(current => ({
      ...current,
      isFetching: true,
    }));

    try {
      const data = await fetch(...args);
      setState(current => {
        if (!append || !current.data) {
          return {
            isFetched: true,
            isFetching: false,
            data,
          };
        }

        if (Array.isArray(current.data) && Array.isArray(data)) {
          return {
            isFetched: true,
            isFetching: false,
            data: [...current.data, ...data] as unknown as T,
          };
        }

        return {
          isFetched: true,
          isFetching: false,
          data: {
            ...current.data,
            ...data,
          },
        };
      });
    } catch (e) {
      console.error(e);
      setState({
        isFetched: true,
        isFetching: false,
        isErrored: true,
      });
    }
  }

  function reset(): void {
    setState({
      isFetching: false,
      isErrored: false,
      isFetched: false,
    });
  }

  return [state, asyncFetch, reset];
}
